import { useState, useEffect } from 'react';
import back_app from '../../lib/back_app';
import platform from '../../lib/platform';
import { cat_id2lbl } from '../../lib/cat';

import xdate from '../../../../../util/date.js';

interface Asset {
  id: string;
  name: string;
  asset_type: string;
  file_url: string;
  mime_type: string;
}

interface Props {
  data: any;
  onSubmit: () => void;
  onBack: () => void;
  onEdit: (step: number) => void;
}

function AssetPreview({ asset }: { asset: Asset }) {
  const [showPreview, setShowPreview] = useState(false);
  const isVideo = asset?.mime_type?.startsWith('video/');

  return (
    <>
      <div
        onClick={() => setShowPreview(true)}
        className="relative bg-gradient-to-br from-gray-900 to-gray-700 rounded-lg overflow-hidden cursor-pointer group"
        style={{ aspectRatio: '16/9' }}
      >
        {isVideo ? (
          <video
            src={asset.file_url}
            className="w-full h-full object-contain"
            muted
          />
        ) : (
          <img
            src={asset.file_url}
            alt={asset.name}
            className="w-full h-full object-contain"
          />
        )}

        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
          <div className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold">
            View Full
          </div>
        </div>

        {}
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded">
            {asset.asset_type === 'video_ad' ? '📺' : '🖼️'}
          </span>
        </div>
      </div>

      {}
      <p className="text-sm font-semibold text-gray-900 mt-2 truncate">{asset.name}</p>

      {}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>
            <div className="bg-black rounded-xl overflow-hidden">
              {isVideo ? (
                <video src={asset.file_url} controls autoPlay className="w-full" />
              ) : (
                <img src={asset.file_url} alt={asset.name} className="w-full" />
              )}
            </div>
            <div className="mt-4 text-white text-center">
              <h3 className="text-xl font-bold">{asset.name}</h3>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function CampaignReview({ data, onSubmit, onBack, onEdit }: Props) {
  const [loading, setLoading] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(true);

  useEffect(() => {
    if (data.selected_asset_ids && data.selected_asset_ids.length > 0) {
      fetchAssets();
    } else {
      setLoadingAssets(false);
    }
  }, [data.selected_asset_ids]);

  const fetchAssets = async () => {
    try {
      const result = await back_app.basset_list();
      if (!result.err && Array.isArray(result)) {
        const assets = result.filter((a: Asset) => 
          data.selected_asset_ids.includes(a.id)
        );
        setSelectedAssets(assets);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleLaunch = async () => {
    setLoading(true);
    await onSubmit();
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Review & Launch
        </h3>
        <p className="text-gray-600">
          Review your campaign details before launching
        </p>
      </div>

      {}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="text-xl">📝</span>
            Basic Information
          </h4>
          <button
            onClick={() => onEdit(1)}
            className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
          >
            Edit
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="text-sm text-gray-600">Campaign Name</div>
            <div className="sm:col-span-2 font-semibold text-gray-900">{data.name}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="text-sm text-gray-600">Category</div>
            <div className="sm:col-span-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                {cat_id2lbl(data.category)}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="text-sm text-gray-600">Description</div>
            <div className="sm:col-span-2 text-gray-900">{data.description}</div>
          </div>
          {data.landing_page && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="text-sm text-gray-600">Landing Page</div>
              <div className="sm:col-span-2">
                <a
                  href={data.landing_page}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm break-all"
                >
                  {data.landing_page}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="text-xl">🎨</span>
            Campaign Assets
            {selectedAssets.length > 0 && (
              <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                {selectedAssets.length} {selectedAssets.length === 1 ? 'asset' : 'assets'}
              </span>
            )}
          </h4>
          <button
            onClick={() => onEdit(2)}
            className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
          >
            Edit
          </button>
        </div>

        {loadingAssets ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-2 text-sm">Loading assets...</p>
          </div>
        ) : selectedAssets.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No assets selected</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {selectedAssets.map((asset) => (
                <div key={asset.id}>
                  <AssetPreview asset={asset} />
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Creators will be able to choose one asset from these {selectedAssets.length} options for their videos
              </p>
            </div>
          </>
        )}
      </div>

      {}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="text-xl">🎯</span>
            Target Audience
          </h4>
          <button
            onClick={() => onEdit(3)}
            className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
          >
            Edit
          </button>
        </div>

        <div className="space-y-4">
          {}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">Countries</div>
            {data.targeting.countries?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.targeting.countries.map((country: string) => (
                  <span key={country} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {country}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">All countries</p>
            )}
          </div>

          {}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">Age Range</div>
            {data.targeting.age_ranges?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.targeting.age_ranges.map((range: number[], idx: number) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                    {range[0]}-{range[1] === 100 ? '65+' : range[1]}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">All ages</p>
            )}
          </div>

          {}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">Gender</div>
            {data.targeting.genders?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.targeting.genders.map((gender: string) => (
                  <span key={gender} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full capitalize">
                    {gender === 'any' ? 'Any Gender' : gender}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">All genders</p>
            )}
          </div>

          {}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">Content Categories</div>
            {data.targeting.content_categories?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.targeting.content_categories.map((id: string) => (
                  <span key={id} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {cat_id2lbl(id)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">All content types</p>
            )}
          </div>
        </div>
      </div>

      {}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="text-xl">💰</span>
            Budget & Schedule
          </h4>
          <button
            onClick={() => onEdit(4)}
            className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
          >
            Edit
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Total Budget</div>
            <div className="text-2xl font-bold text-gray-900">
              ${data.budget_info.total_budget.toLocaleString()}
            </div>
          </div>

          <div className="bg-white rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Est. sponsorships</div>
            <div className="text-2xl font-bold text-blue-600">
              {platform.spons_est(data.budget_info.total_budget || 0,
                data.budget_info.creator_tiers).toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              at {platform.cps_lbl(data.budget_info.creator_tiers)} per sponsorship
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Schedule</div>
          {data.schedule.is_always_on ? (
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              <span className="font-semibold">Always Active</span>
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Start:</span>
                <span className="font-semibold text-gray-900">
                  {data.schedule.start_date
                    ? xdate.fmt_lbl_short(new Date(data.schedule.start_date))
                    : '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">End:</span>
                <span className="font-semibold text-gray-900">
                  {data.schedule.end_date
                    ? xdate.fmt_lbl_short(new Date(data.schedule.end_date))
                    : '—'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-4 sm:p-6">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 mb-2">Ready to Launch?</h4>
            <p className="text-sm text-gray-700 mb-2">
              Your campaign will be created and made available to matching creators immediately. You can pause or edit your campaign anytime from the dashboard.
            </p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>✓ Creators will see your campaign in their browse page</li>
              <li>✓ They can choose from {selectedAssets.length} {selectedAssets.length === 1 ? 'asset' : 'assets'}</li>
              <li>✓ AI will find the best placement moments automatically</li>
              <li>✓ You'll start receiving engagement data right away</li>
            </ul>
          </div>
        </div>
      </div>

      {}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleLaunch}
          disabled={loading}
          className="w-full sm:flex-1 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Launching...
            </>
          ) : (
            <>
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd"/>
              </svg>
              Launch Campaign
            </>
          )}
        </button>
      </div>
    </div>
  );
}